import React, { useEffect, useState } from 'react';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';

const AttendanceFilters = ({ onFilterChange, initialFilters }) => {
  const [filters, setFilters] = useState(initialFilters || {});

  // Sync state when initialFilters changes (e.g. parent resets or month changes)
  useEffect(() => {
    setFilters(initialFilters || {});
  }, [initialFilters]);

  const handleChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
  };

  const handleApply = (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (filters.startDate && filters.endDate && filters.startDate > filters.endDate) {
      toast.warning('Start Date cannot be after End Date. Swapping dates automatically.');
      const corrected = {
        ...filters,
        startDate: filters.endDate,
        endDate: filters.startDate
      };
      setFilters(corrected);
      onFilterChange(corrected);
      return;
    }

    onFilterChange(filters);
  };

  const handleClear = () => {
    const emptyFilters = {};
    setFilters(emptyFilters);
    onFilterChange(emptyFilters);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleApply(e);
    }
  };

  return (
    <div className="mb-4 p-3 p-md-4 border-0 shadow-sm bg-white rounded-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="fw-bold text-secondary mb-0">Filter Attendance Data</h6>
        {(filters.startDate || filters.endDate || filters.status || filters.isLate || filters.isEarly || filters.isMissPunch || filters.isHalfDay || filters.isAbsent) && (
          <span className="badge bg-primary-subtle text-primary border border-primary-subtle rounded-pill px-2 py-1 small fw-semibold">
            Filters Active
          </span>
        )}
      </div>

      <Form onSubmit={handleApply} onKeyDown={handleKeyDown}>
        <Row className="g-3">
          {/* Date Range Selection */}
          <Col xs={6} sm={6} md={4} lg={2}>
            <Form.Group>
              <Form.Label className="fw-bold small text-muted">Start Date</Form.Label>
              <Form.Control
                type="date"
                value={filters.startDate || ''}
                onChange={(e) => handleChange('startDate', e.target.value)}
                className="form-control-sm border-0 bg-light"
                title="Select start date (From)"
              />
            </Form.Group>
          </Col>
          <Col xs={6} sm={6} md={4} lg={2}>
            <Form.Group>
              <Form.Label className="fw-bold small text-muted">End Date</Form.Label>
              <Form.Control
                type="date"
                value={filters.endDate || ''}
                onChange={(e) => handleChange('endDate', e.target.value)}
                className="form-control-sm border-0 bg-light"
                title="Select end date (To)"
              />
            </Form.Group>
          </Col>

          {/* Status Dropdown */}
          <Col xs={12} sm={6} md={4} lg={2}>
            <Form.Group>
              <Form.Label className="fw-bold small text-muted">Status</Form.Label>
              <Form.Select
                value={filters.status || ''}
                onChange={(e) => handleChange('status', e.target.value)}
                className="form-select-sm border-0 bg-light"
              >
                <option value="">All Status</option>
                <option value="Present">Present</option>
                <option value="Absent">Absent</option>
                <option value="Late">Late</option>
                <option value="Early Exit">Early Going</option>
                <option value="Miss Punch">Miss Punch</option>
                <option value="Half Days">Half Day</option>
                <option value="Leave">Leave</option>
                <option value="Weekly Off">Weekly Off</option>
                <option value="Festival">Festival</option>
                <option value="Extra Work">Extra Work</option>
              </Form.Select>
            </Form.Group>
          </Col>

          {/* Quick Filters */}
          <Col xs={12} md={8} lg={4}>
            <Form.Label className="fw-bold small text-muted">Quick Insights</Form.Label>
            <div className="d-flex flex-wrap gap-2 gap-sm-3 py-1">
              <Form.Check
                type="checkbox"
                id="late-commers"
                label={<span className="small">Late</span>}
                checked={filters.isLate || false}
                onChange={(e) => handleChange('isLate', e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                id="early-going"
                label={<span className="small">Early Going</span>}
                checked={filters.isEarly || false}
                onChange={(e) => handleChange('isEarly', e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                id="miss-punch"
                label={<span className="small">Miss Punch</span>}
                checked={filters.isMissPunch || false}
                onChange={(e) => handleChange('isMissPunch', e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                id="half-days"
                label={<span className="small">Half Days</span>}
                checked={filters.isHalfDay || false}
                onChange={(e) => handleChange('isHalfDay', e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                id="absent"
                label={<span className="small">Absent</span>}
                checked={filters.isAbsent || false}
                onChange={(e) => handleChange('isAbsent', e.target.checked)}
              />
            </div>
          </Col>

          {/* Action Buttons */}
          <Col xs={12} sm={6} md={4} lg={2} className="d-flex align-items-end gap-2 mt-2 mt-md-0">
            <Button
              type="submit"
              variant="primary"
              size="sm"
              className="px-3 rounded-pill flex-fill flex-md-grow-0 fw-bold shadow-sm"
            >
              Apply
            </Button>
            <Button
              type="button"
              variant="light"
              onClick={handleClear}
              size="sm"
              className="px-3 rounded-pill flex-fill flex-md-grow-0 fw-bold border shadow-sm"
            >
              Reset
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default AttendanceFilters;
